import { useCallback, useMemo } from 'react'
import {
  Highlight,
  useInfiniteHits,
} from 'react-instantsearch'
import DOMPurify from 'isomorphic-dompurify'

import LinkComponent from "@/components/link-component"
import { useTranslations } from "@/utils/locale"
import Routes from "@/constants/routes"
import { cx } from "@/utils/strings"

import DblAngleRightIcon from '@/icons/bwi/dbl-angle-right.svg'
import ArrowCircleRightIcon from '@/icons/bwi/arrow-circle-right.svg'
import HashtagIcon from '@/icons/bwi/hashtag.svg'

import { HitProps } from "./types"

type HitPreviewProps = {
  hit: HitProps
  onLinkClick?: (hit: HitProps, e: React.MouseEvent<HTMLAnchorElement>) => void
}

export default function HitPreview({ hit, onLinkClick }: HitPreviewProps) {
  const { t } = useTranslations()
  const { sendEvent } = useInfiniteHits<HitProps>()

  const highlightedBody = useMemo(() => {
    if (!hit?._highlightResult?.body) return "";
    const parser = new DOMParser();
    let body: string = (hit?._highlightResult?.body as any).value || ""
    // Escape <mark> and </mark> tags
    body = body
      .replace(/<mark>/g, '&lt;mark class="ais-Highlight-highlighted dark:text-secondary-900"&gt;')
      .replace(/<\/mark>/g, '&lt;/mark&gt;');
    const decoded = parser.parseFromString(body, "text/html").documentElement.textContent;
    return DOMPurify.sanitize(decoded)
  }, [hit]);

  const isSection = !!hit.hash
  // Remove the last breadcrumb if it's the same as the title
  const breadcrumbs = hit.breadcrumbs?.slice(0, -1) || []

  const Link = useCallback(
    ({ to, children, className }: { to: string; children: React.ReactNode; className?: string }) => (
      <LinkComponent
        to={to}
        className={className}
        onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
          onLinkClick?.(hit, e)
          sendEvent('click', hit, 'Result Clicked')
          sendEvent('conversion', hit, 'Result Converted')
        }}
      >
        {children}
      </LinkComponent>
    ),
    [hit, onLinkClick]
  )

  return (
    <div className="hidden md:block w-1/2 pt-10 px-6 pb-6 overflow-auto bg-secondary-50 dark:bg-secondary-800">
      <Link to={`${hit.url}${hit.hash ? `#${hit.hash}` : ''}`} className="hover:underline">
        <div className={cx('text-xs text-secondary-500 dark:text-secondary-400 mb-4')}>
            {!hit.url.toLowerCase().startsWith('http') && Routes.hostname}
            {hit.url}
            {hit.hash && `#${hit.hash}`}
        </div>
      </Link>
      <div className="mb-4">
        {breadcrumbs.map((breadcrumb, index) => (
          <div key={index} className="inline-flex items-center justify-center whitespace-pre">
            {index > 0 && <DblAngleRightIcon className="w-2 h-2 mx-2" />}
            {breadcrumb}
          </div>
        ))}
      </div>
      
      {!isSection && (
        <Link to={hit.url} className="hover:text-primary-700 dark:hover:text-secondary-300 transition-colors duration-200">
          <h3>
            <Highlight
              hit={hit}
              attribute="title"
              classNames={{
                highlighted: cx('dark:text-secondary-900'),
              }}
            />
          </h3>
        </Link>
      )}
      {isSection && (
        <>
          <Link to={hit.url} className="hover:text-primary-700 dark:hover:text-secondary-300 transition-colors duration-200">
            <h3 className="mb-8">
              {hit.breadcrumbs?.[hit.breadcrumbs.length - 1]}
            </h3>
          </Link>
          <Link to={`${hit.url}#${hit.hash}`} className="hover:text-primary-700 dark:hover:text-secondary-300 transition-colors duration-200">
            <h4 className="h6 flex">
              <HashtagIcon className="inline-block w-6 h-6 mr-2" />
              <Highlight
                hit={hit}
                attribute="title"
                classNames={{
                  highlighted: cx('dark:text-secondary-900'),
                }}
              />
            </h4>
          </Link>
        </>
      )}
      {highlightedBody && <div className="text-base mb-12 [&_ul]:pl-6 [&_ol]:pl-6" dangerouslySetInnerHTML={{ __html: highlightedBody }} />}
      {(hit.toc?.length ?? 0) > 0 && (
        <>
          <p className="text-base text-secondary-900 dark:text-white uppercase mb-2">{t('On this page:')}</p>
          {hit.toc?.map((toc, index) => {
            return (
              <Link
                key={index}
                className={cx(
                  'flex items-center text-base text-neutral-600 dark:text-white leading-8 hover:text-primary-700 dark:hover:text-secondary-300 transition-colors duration-200',
                  toc.level === 3 && 'pl-4',
                  toc.hash === `#${hit.hash}` && 'text-primaryBlue! hover:text-primary-700! dark:hover:text-secondary-300!',
                )}
                to={`${hit.url}${toc.hash}`}
              >
                <ArrowCircleRightIcon className="text-primaryBlue w-4 h-4 mr-2" />
                {toc.title}
              </Link>
            )
          })}
        </>
      )}
    </div>
  )
}
